// useWaveformColors — резолвит цвета waveform-канваса из CSS-токенов темы.
//
// Canvas рисуется императивно (fillStyle), Tailwind-классы там недоступны.
// Чтобы waveform не «выпадал» из дизайн-системы и корректно перекрашивался
// в тёмной теме, читаем computed-значения CSS-переменных «Картотека»:
//   - played — пройденная часть = акцент --primary
//   - track  — непройденная часть = приглушённый --ink-3 (мета-цвет)
// Пересчитываются при смене темы (подписка на useThemeStore).

import { useEffect, useState } from "react";
import { useThemeStore } from "@/stores/theme";

export interface WaveformColors {
	/** Цвет пройденных столбиков (акцент). */
	played: string;
	/** Цвет непройденных столбиков (приглушённый). */
	track: string;
}

// Дефолты «Картотека» (light) — fallback до первого resolve в DOM.
const FALLBACK: WaveformColors = {
	played: "#2f6fd4",
	track: "rgba(135, 141, 160, 0.4)",
};

function readColors(): WaveformColors {
	if (typeof window === "undefined") return FALLBACK;
	const cs = getComputedStyle(document.documentElement);
	const primary = cs.getPropertyValue("--primary").trim();
	const ink3 = cs.getPropertyValue("--ink-3").trim();
	return {
		played: primary || FALLBACK.played,
		// --ink-3 — сплошной цвет; приглушаем через color-mix до полупрозрачности.
		track: ink3 ? `color-mix(in srgb, ${ink3} 42%, transparent)` : FALLBACK.track,
	};
}

/**
 * Возвращает цвета для отрисовки waveform, синхронизированные с активной
 * темой. Объект стабилен между ререндерами, пока тема не меняется.
 */
export function useWaveformColors(): WaveformColors {
	const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
	const [colors, setColors] = useState<WaveformColors>(FALLBACK);

	// resolvedTheme в deps — намеренный re-trigger: при переключении light↔dark
	// CSS-переменные на <html> уже сменились, перечитываем computed-цвета.
	// biome-ignore lint/correctness/useExhaustiveDependencies: resolvedTheme — триггер пересчёта, не значение внутри эффекта.
	useEffect(() => {
		setColors(readColors());
	}, [resolvedTheme]);

	return colors;
}
